import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Target,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  Brain,
  ChartLineUp,
  MagnifyingGlass,
  Coins,
  Funnel,
  Repeat,
  Question,
  CaretDown,
  ChartBar,
  Lightbulb,
  Stack,
  Crosshair,
  Scales,
  ArrowsClockwise,
  Sliders
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const HowToFindTradingSetup = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'How to Find a Profitable Trading Setup | Complete Guide',
      description: 'Learn how to find and improve a trading setup using backtesting, data analysis, and structured evaluation. Discover what actually works.',
      path: '/how-to-find-a-trading-setup',
      lang: 'en',
      alternates: getArticleAlternates('/how-to-find-a-trading-setup'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "What is the best trading setup?",
      answer: "There's no universally 'best' trading setup. The best setup for you is one that fits your personality, risk tolerance, and schedule—AND has been validated through data on the specific assets you trade. A breakout strategy might work excellently for one trader on BTC while failing for another on altcoins. The real answer: the best setup is one you've tested extensively, refined continuously, and can execute consistently."
    },
    {
      question: "How many trades are needed to validate a setup?",
      answer: "A minimum of 30-50 trades is often cited, but 100+ trades provides much stronger statistical confidence. The key factors are: consistency of conditions (same asset, same timeframe, same market regime), honest tracking of all trades (not just winners), and enough time for varied market conditions (trending, ranging, volatile). One week of trades isn't enough—you need months of data across different conditions."
    },
    {
      question: "Why does a setup work on one coin but not another?",
      answer: "Different assets have different characteristics: liquidity, volatility, market participants, and correlation to broader markets. A setup that works on BTC (high liquidity, mature market) may fail on low-cap altcoins (thin order books, susceptible to manipulation). Additionally, correlations between assets can create false signals. This is why professional traders segment their analysis by asset or asset group rather than lumping everything together."
    },
    {
      question: "Does adding more indicators improve results?",
      answer: "Not necessarily. Adding indicators typically reduces trade frequency because you're requiring more conditions to align. Fewer trades doesn't automatically mean better performance—you might filter out good opportunities while keeping mediocre ones. The goal is finding the minimum effective set of conditions that identify your edge, not creating a complex system that rarely triggers. Quality of signals matters more than quantity of indicators."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Try GENI Free
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
            <Target size={16} weight="fill" />
            <span>Trading Strategy</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            How to Find a Profitable Trading Setup
            <span className="block text-white/60 text-2xl md:text-3xl mt-2">
              (What Actually Works)
            </span>
          </h1>
          
          <p className="text-lg text-white/70 leading-relaxed">
            Everyone's searching for the "perfect setup"—the holy grail pattern that prints money 
            on demand. Here's the uncomfortable truth: it doesn't exist. But <strong className="text-white">profitable 
            trading setups</strong> do exist, and finding them requires a fundamentally different 
            approach than most traders take. First, you need to understand <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline">how to analyze your trades</Link> properly.
          </p>
        </header>

        {/* Section 1 */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">01</span>
            What Is a Trading Setup?
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              A <strong className="text-white">trading setup</strong> is a repeatable pattern with 
              clearly defined rules: entry criteria, exit criteria (both profit target and stop loss), 
              and position sizing guidelines. It's not a vague idea like "buy when it looks bullish"—it's 
              a specific, documented playbook.
            </p>
            <p className="text-white/70 leading-relaxed">
              Good setups answer these questions: When do I enter? Where is my stop? Where is my target? 
              How much do I risk? If you can't answer all four for every trade, you don't have a setup—you 
              have a guess.
            </p>
          </div>
          
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {[
              { label: "Entry Criteria", desc: "Specific conditions that trigger the trade" },
              { label: "Stop Loss", desc: "Predefined exit for risk management" },
              { label: "Take Profit", desc: "Target level based on R:R ratio" },
              { label: "Position Size", desc: "Risk-adjusted sizing rules" }
            ].map((item, index) => (
              <div key={index} className="bg-[#121212] border border-white/10 rounded-lg p-4">
                <h4 className="font-medium text-sm mb-1 text-[#00E676]">{item.label}</h4>
                <p className="text-white/50 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 2 */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">02</span>
            Why There Is No "Perfect" Setup
          </h2>
          
          <div className="bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-8 mb-6">
            <p className="text-white/80 leading-relaxed">
              Even the <strong className="text-white">best trading setup</strong> will fail sometimes. 
              Market conditions change, liquidity shifts, and execution varies. A setup with a 65% win 
              rate still loses 35% of the time. That's not a flaw—that's probability.
            </p>
          </div>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              The search for perfection leads traders to constantly abandon strategies after a few losses, 
              never giving anything time to prove itself. They jump from setup to setup, always chasing, 
              never mastering. This is one of the core reasons <Link to="/why-traders-fail-to-improve" className="text-[#00E676] hover:underline">why traders fail to improve</Link>.
            </p>
            <p className="text-white/70 leading-relaxed">
              The goal isn't finding a perfect setup. It's finding a <em className="text-white/90">good enough</em> setup 
              and refining it over time with data.
            </p>
          </div>
        </section>

        {/* Section 3 */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">03</span>
            The Role of Backtesting
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              Before risking real money, you need to test your <strong className="text-white">trading setup 
              strategy</strong> on historical data. Tools like TradingView make this accessible, but backtesting 
              requires discipline to be meaningful.
            </p>
          </div>
          
          <div className="my-8 space-y-4">
            {[
              {
                title: "Use Sufficient Sample Size",
                description: "One or two winning trades prove nothing. You need 50-100+ trades minimum to draw meaningful conclusions.",
                icon: ChartBar
              },
              {
                title: "Test Across Market Conditions",
                description: "A setup that only works in bull markets isn't robust. Test across trends, ranges, and volatile periods.",
                icon: TrendUp
              },
              {
                title: "Avoid Confirmation Bias",
                description: "Don't cherry-pick examples. Mark every valid setup, not just the ones that worked beautifully.",
                icon: Warning
              },
              {
                title: "Document Everything",
                description: "Track entry, exit, R:R, and outcome for each backtested trade. Your memory will lie to you.",
                icon: MagnifyingGlass
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-white/10 rounded-lg p-5"
              >
                <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={20} weight="duotone" className="text-[#00E676]" />
                </div>
                <div>
                  <h4 className="font-medium mb-1">{item.title}</h4>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              Backtesting isn't about proving your setup works—it's about finding where it works, 
              where it fails, and whether the edge is large enough to trade profitably after costs.
            </p>
          </div>
        </section>

        {/* Section 4 */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">04</span>
            Consistency Comes From Volume
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              A setup that won 5 out of 5 trades last week is meaningless. A setup that won 60 out of 
              100 trades over three months is meaningful. The difference is statistical significance.
            </p>
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">Trading setup analysis</strong> requires volume—enough trades 
              to distinguish signal from noise. Small samples are dominated by randomness. You might think 
              you found an edge when you just got lucky.
            </p>
          </div>
          
          <div className="my-8 bg-[#121212] border border-white/10 rounded-lg p-6">
            <h4 className="font-semibold mb-4 flex items-center gap-2">
              <Repeat size={18} className="text-purple-400" />
              Sample Size Reality Check
            </h4>
            <div className="grid sm:grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-red-500/10 rounded-lg">
                <p className="text-2xl font-bold text-red-400">10</p>
                <p className="text-xs text-white/50">trades = noise</p>
              </div>
              <div className="p-4 bg-orange-500/10 rounded-lg">
                <p className="text-2xl font-bold text-orange-400">30-50</p>
                <p className="text-xs text-white/50">trades = emerging pattern</p>
              </div>
              <div className="p-4 bg-green-500/10 rounded-lg">
                <p className="text-2xl font-bold text-green-400">100+</p>
                <p className="text-xs text-white/50">trades = reliable data</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5 */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">05</span>
            Why the Same Setup Performs Differently Across Assets
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              A breakout setup on BTC is not the same as a breakout setup on a low-cap altcoin. 
              The pattern might look identical, but the underlying dynamics are completely different.
            </p>
          </div>
          
          <div className="my-8 grid sm:grid-cols-2 gap-4">
            {[
              { factor: "Liquidity", impact: "Thin order books cause slippage and false breakouts" },
              { factor: "Volatility", impact: "Higher volatility requires wider stops, changing R:R" },
              { factor: "Market Participants", impact: "Institutional vs retail-dominated assets behave differently" },
              { factor: "Correlation", impact: "Altcoins often move with BTC, creating false independent signals" }
            ].map((item, index) => (
              <div key={index} className="bg-[#121212] border border-white/10 rounded-lg p-5">
                <h4 className="font-medium text-sm mb-2 text-[#00E676]">{item.factor}</h4>
                <p className="text-white/60 text-xs leading-relaxed">{item.impact}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              Many traders fail because they lump all their trades together—mixing BTC, ETH, and random 
              altcoins into one dataset. The result? Polluted data that tells them nothing useful. 
              If you're trading crypto, read our guide on the <Link to="/crypto-trading-journal" className="text-[#00E676] hover:underline">best crypto trading journal</Link> for 
              asset-specific tracking.
            </p>
          </div>
        </section>

        {/* Section 6 */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">06</span>
            The Problem With Grouping Assets
          </h2>
          
          <div className="bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/20 rounded-lg p-8 mb-6">
            <p className="text-white/80 leading-relaxed mb-4">
              <strong className="text-white">Example:</strong> Your "breakout" setup shows a 55% win rate 
              across all trades. Sounds okay, right?
            </p>
            <p className="text-white/70 leading-relaxed mb-4">
              But when you segment the data:
            </p>
            <ul className="space-y-2 text-white/70">
              <li className="flex items-center gap-2">
                <CheckCircle size={14} weight="fill" className="text-green-400" />
                BTC breakouts: 68% win rate
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={14} weight="fill" className="text-green-400" />
                ETH breakouts: 62% win rate
              </li>
              <li className="flex items-center gap-2">
                <Warning size={14} weight="fill" className="text-red-400" />
                Altcoin breakouts: 38% win rate
              </li>
            </ul>
          </div>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              The aggregated 55% hides the fact that your setup <em className="text-white/90">works great</em> on 
              majors and <em className="text-white/90">fails badly</em> on alts. Without segmentation, you'd 
              never know—and you'd keep bleeding money on trades you should avoid entirely.
            </p>
            <p className="text-white/70 leading-relaxed">
              <strong className="text-white">Proper trading setup analysis</strong> requires breaking 
              down performance by asset, asset class, or asset group.
            </p>
          </div>
        </section>

        {/* Section 7 */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">07</span>
            One Setup Can Have Multiple Variations
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              Consider a base concept like trading Elliott Wave retracements. The core idea is the same, 
              but the specific trigger can vary dramatically:
            </p>
          </div>
          
          <div className="my-8 space-y-3">
            {[
              "Wave 2 retracement + RSI oversold confirmation",
              "Wave 2 retracement + CVD divergence",
              "Wave 2 retracement + RSI + CVD combined",
              "Wave 2 retracement + volume spike on reversal candle"
            ].map((variation, index) => (
              <div 
                key={index}
                className="flex items-center gap-3 bg-[#121212] border border-white/10 rounded-lg px-5 py-3"
              >
                <Stack size={16} className="text-purple-400 flex-shrink-0" />
                <p className="text-white/70 text-sm">{variation}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              Each variation produces different results. One might have a higher win rate but fewer opportunities. 
              Another might trade more frequently but with lower accuracy. You can't know which is optimal 
              without testing each one independently.
            </p>
          </div>
        </section>

        {/* Section 8 */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">08</span>
            Indicators and Trade Frequency
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              Here's a common trap: adding more indicators to "filter" bad trades. In theory, requiring 
              RSI + MACD + volume + momentum alignment should produce better signals. In practice, 
              it often produces almost no signals at all.
            </p>
          </div>
          
          <div className="my-8 bg-[#121212] border border-white/10 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Sliders size={20} className="text-[#00E676]" />
              <h4 className="font-semibold">The Filtering Paradox</h4>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-white/80 mb-2">More indicators →</p>
                <ul className="space-y-1 text-white/60 text-xs">
                  <li>• Fewer trade opportunities</li>
                  <li>• Potentially higher win rate</li>
                  <li>• But possibly missing the best trades</li>
                  <li>• Harder to get meaningful sample size</li>
                </ul>
              </div>
              <div>
                <p className="text-sm font-medium text-white/80 mb-2">Fewer indicators →</p>
                <ul className="space-y-1 text-white/60 text-xs">
                  <li>• More trade opportunities</li>
                  <li>• Lower win rate (typically)</li>
                  <li>• Captures more valid setups</li>
                  <li>• Easier to validate with volume</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              The goal is finding the <em className="text-white/90">minimum effective</em> set of conditions—enough 
              to identify your edge without over-filtering valid opportunities. More isn't always better.
            </p>
          </div>
        </section>

        {/* Section 9 */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">09</span>
            What Actually Makes a Setup Work
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            {[
              {
                title: "Data-Backed Validation",
                description: "Real trade data, not theoretical results or cherry-picked examples",
                icon: ChartLineUp
              },
              {
                title: "Consistent Execution",
                description: "Taking every valid signal, not just the ones that 'feel right'",
                icon: ArrowsClockwise
              },
              {
                title: "Proper Segmentation",
                description: "Understanding performance by asset, timeframe, and market condition",
                icon: Funnel
              },
              {
                title: "Continuous Refinement",
                description: "Treating your setup as a living system that evolves with data",
                icon: Brain
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-[#00E676]/20 rounded-lg p-6"
              >
                <item.icon size={24} weight="duotone" className="text-[#00E676] mb-3" />
                <h4 className="font-semibold mb-2">{item.title}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed">
              A <strong className="text-white">profitable trading setup</strong> isn't magic. It's the 
              result of systematic testing, honest tracking, and continuous improvement based on 
              real performance data.
            </p>
          </div>
        </section>

        {/* Section 10 */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center gap-3">
            <span className="text-[#00E676] text-lg font-mono">10</span>
            The Real Goal: Improving Good Setups
          </h2>
          
          <div className="prose prose-invert max-w-none space-y-4">
            <p className="text-white/70 leading-relaxed">
              You don't need to find a perfect setup. You need to find a <em className="text-white/90">good enough</em> setup 
              and make it better. This means:
            </p>
          </div>
          
          <div className="my-8 space-y-3">
            {[
              "Identify where the setup works best (which assets, timeframes, conditions)",
              "Cut the scenarios where it consistently fails",
              "Improve execution by understanding your personal patterns",
              "Refine entry/exit rules based on post-trade analysis"
            ].map((point, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle size={18} weight="fill" className="text-[#00E676] mt-0.5 flex-shrink-0" />
                <p className="text-white/70 leading-relaxed">{point}</p>
              </div>
            ))}
          </div>
          
          <div className="bg-gradient-to-br from-[#00E676]/10 to-purple-500/10 border border-[#00E676]/30 rounded-lg p-8 mt-8">
            <p className="text-white/80 leading-relaxed mb-4">
              With <strong className="text-white">GENI</strong>, traders can easily compare different setups 
              across different asset groups. The platform automatically organizes and visualizes performance 
              data, making it much easier to identify what actually works.
            </p>
            <p className="text-white/70 leading-relaxed">
              Instead of guessing, traders can see which setups perform best on specific coins or groups, 
              and continuously improve already good strategies.
            </p>
          </div>
        </section>

        {/* Internal Links */}
        <section className="mb-14">
          <div className="p-4 border-l-2 border-[#00E676] bg-[#00E676]/5 rounded-r-lg">
            <p className="text-white/80 text-sm mb-3">
              <strong>Related Articles:</strong>
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline text-sm">
                How to Analyze Your Trades →
              </Link>
              <Link to="/automated-trading-journal" className="text-[#00E676] hover:underline text-sm">
                What Is an Automated Journal? →
              </Link>
              <Link to="/crypto-trading-journal" className="text-[#00E676] hover:underline text-sm">
                Best Crypto Trading Journal →
              </Link>
              <Link to="/why-traders-fail-to-improve" className="text-[#00E676] hover:underline text-sm">
                Why Traders Fail to Improve →
              </Link>
              <Link to="/manual-vs-automated-trading-journal" className="text-[#00E676] hover:underline text-sm">
                Manual vs Automated Journals →
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="mb-14">
          <h2 className="text-xl font-semibold mb-6">Related Articles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/how-to-analyze-your-trades" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Analyze Your Trades</h3>
              <p className="text-white/50 text-sm">Step-by-step guide to reviewing trades like a pro.</p>
            </Link>
            <Link to="/automated-trading-journal" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">What Is an Automated Journal?</h3>
              <p className="text-white/50 text-sm">Learn why automation beats manual tracking.</p>
            </Link>
            <Link to="/why-traders-fail-to-improve" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Why Traders Fail to Improve</h3>
              <p className="text-white/50 text-sm">Common mistakes that keep traders stuck.</p>
            </Link>
            <Link to="/manual-vs-automated-trading-journal" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Manual vs Automated Journals</h3>
              <p className="text-white/50 text-sm">Which journaling method is right for you?</p>
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Question size={28} weight="duotone" className="text-purple-400" />
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium pr-4">{faq.question}</span>
                  <CaretDown 
                    size={18} 
                    className={`text-white/40 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} 
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 pt-0">
                    <p className="text-white/60 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section>
          <div className="bg-gradient-to-br from-purple-500/10 via-[#121212] to-[#00E676]/10 border border-white/10 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
              <Target size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Stop Guessing. Start Improving.
            </h2>
            
            <p className="text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
              If you want to stop guessing and start improving your setups with real data, 
              using a structured analysis system is essential.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Start Tracking Free
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Learn More
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              No credit card required · Free tier available
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "How to Find a Profitable Trading Setup (What Actually Works)",
              "description": "Learn how to find and improve a trading setup using backtesting, data analysis, and structured evaluation. Discover what actually works.",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0],
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://genitradeapp.com/how-to-find-a-trading-setup"
              }
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </article>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowToFindTradingSetup;
